import { motion } from 'framer-motion';
import { 
  Search, 
  Users, 
  Monitor, 
  Target, 
  BarChart3,
  ArrowRight,
  Phone,
  CheckCircle2,
  Globe,
  TrendingUp,
  FileText,
  Lightbulb,
  Award,
  Swords
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const researchMethods = [
  {
    icon: Search,
    title: 'Desk Research',
    description: 'Analisis data sekunder dari sumber kredibel (BPS, Euromonitor, Statista, dll.)'
  },
  {
    icon: Users,
    title: 'Survey Online, FGD & Interview',
    description: 'Insight langsung dari target audiens yang relevan'
  },
  {
    icon: Monitor,
    title: 'Digital Platform Observation',
    description: 'Analisis tren dan perilaku konsumen di platform online'
  },
  {
    icon: Target,
    title: 'Market Sizing & Perceptual Mapping',
    description: 'Menentukan potensi pasar dan posisi merek Anda'
  },
  {
    icon: BarChart3,
    title: 'Industrial Analysis (PESTLE & Porter)',
    description: 'Memahami dinamika industri dan faktor risiko eksternal'
  }
];

const companyBenefits = [
  {
    icon: Lightbulb,
    title: 'Framework Strategi Pemasaran',
    description: 'Membantu secara spesifik mendesign framework strategi pemasaran dan menyusun actionable plan yang tepat untuk usaha Anda'
  },
  {
    icon: Globe,
    title: 'Insight Global & Lokal',
    description: 'Insight Market & Marketing Research & Plan dalam konteks lokal atau regional atau internasional'
  },
  {
    icon: TrendingUp,
    title: 'Decision Making Support',
    description: 'Insight berkualitas untuk decision making & perencanaan strategi pemasaran jangka pendek - panjang'
  },
  {
    icon: CheckCircle2,
    title: 'Validasi Produk & Go-to-Market',
    description: 'Validasi produk & go-to-market plan sebelum peluncuran'
  },
  {
    icon: Award,
    title: 'Assessment Ekspansi Bisnis',
    description: 'Membantu bisnis Anda melakukan assessment ekspansi dengan memperhitungkan market size, revenue forecasting dan feasibility investasi'
  }
];

const packages = [
  {
    name: 'Basic Package',
    description: 'Untuk memahami gambaran umum pasar dan potensi bisnis Anda',
    features: [
      'Market overview sizing',
      'Key trends analysis',
      'Competitiveness assessment',
      'SWOT analysis'
    ],
    output: 'Market overview, sizing, key trends, competitiveness, SWOT',
    icon: FileText,
    popular: false
  },
  {
    name: 'Premium Package',
    description: 'Analisis pasar menyeluruh untuk perumusan strategi yang komprehensif',
    features: [
      'Consumer archetype canvas',
      'TOWS matrix',
      'Perceptual mapping',
      'Strategy recommendation'
    ],
    output: 'Consumer archetype canvas, TOWS matrix, perceptual mapping, strategy recommendation',
    icon: Award,
    popular: true
  },
  {
    name: 'Marketing Strategic Optimization',
    description: 'Maksimalkan potensi bisnis dengan strategi berbasis data dan analisis kebutuhan konsumen',
    features: [
      'Behavioral archetype',
      'Unmet needs analysis',
      'Competitor mapping',
      'Marketing strategy canvas'
    ],
    output: 'Behavioral archetype, unmet needs analysis, competitor mapping, marketing strategy canvas',
    icon: TrendingUp,
    popular: false
  },
  {
    name: 'MarketPlus: Strategy Development Plan',
    description: 'Perumusan strategi pemasaran jangka pendek-menengah dengan sistem KPI-based dan budgeting plan',
    features: [
      'STP Analysis',
      'Customer Journey Mapping',
      'Value Delivery Strategy',
      'KPI-based Plan'
    ],
    output: 'STP Analysis, Customer Journey Mapping, Value Delivery Strategy, KPI-based Plan',
    icon: Target,
    popular: false
  },
  {
    name: 'Industry Research & Analysis',
    description: 'Analisis makro untuk strategi ekspansi dan mitigasi risiko sistemik industri',
    features: [
      'PESTLE Analysis',
      'Porter\'s 5 Forces',
      'Risk Mapping',
      'Strategic Implication Report'
    ],
    output: 'PESTLE, Porter\'s 5 Forces, Risk Mapping, Strategic Implication Report',
    icon: BarChart3,
    popular: false
  },
  {
    name: 'Market Validation',
    description: 'Uji kelayakan ide bisnis atau produk bisnis anda sebelum eksekusi menggunakan data pasar, demand analysis, dan consumer insight untuk meminimalkan risiko gagal.',
    features: [
      'Idea/Product Feasibility Testing',
      'Demand Analysis & Market Potential',
      'Consumer Insight & Validation',
      'Risk Minimization Strategy'
    ],
    output: 'Validated business idea, demand analysis report, consumer insights, go/no-go recommendation',
    icon: Lightbulb,
    popular: false
  },
  {
    name: 'Competitive Landscape Mapping',
    description: 'Pemetaan kompetitor secara komprehensif: positioning, pricing, keunggulan \u0026 kelemahan, hingga market gap agar anda masuk dengan strategi yang lebih tajam dan unggul.',
    features: [
      'Competitor Positioning Analysis',
      'Pricing Strategy Mapping',
      'Strengths \u0026 Weaknesses Assessment',
      'Market Gap Identification'
    ],
    output: 'Competitor landscape map, pricing analysis, SWOT comparison, market opportunity report',
    icon: Swords,
    popular: false
  }
];

export function MarketMarketingResearch() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: methodsRef, isInView: methodsInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isInView: benefitsInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: packagesRef, isInView: packagesInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center"
        >
          <source src="/market-research-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85" />
        
        <div className="relative max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">02</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Market & Marketing Research
            </h1>
            
            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              Layanan riset kami dirancang untuk membantu bisnis memahami pasar dengan pendekatan 
              <strong className="text-primary-400"> data-driven dan actionable</strong>. 
              Kami tidak hanya mengumpulkan data, tapi juga 
              <strong className="text-primary-400"> menerjemahkannya menjadi strategi nyata</strong> 
              yang mendorong pertumbuhan.
            </p>
            
            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Research Methods Section */}
      <section ref={methodsRef} className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={methodsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Metode yang Kami Gunakan
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Kombinasi metode riset modern untuk menghasilkan insight yang akurat dan actionable
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {researchMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 20 }}
                animate={methodsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <method.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{method.title}</h3>
                <p className="text-white/60 text-sm">{method.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/market-benefits-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />
        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Manfaat Untuk Perusahaan
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Insight berkualitas yang mendorong pertumbuhan bisnis berkelanjutan
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companyBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-600/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-5 h-5 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-white/60 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 glass rounded-2xl p-8 text-center"
          >
            <p className="text-white/80 text-lg">
              Kami juga dapat membantu bisnis Anda memahami seberapa besar potensi pasar dan di mana posisi ideal brand Anda dibandingkan kompetitor, 
              sehingga keputusan ekspansi lebih tepat dan terukur.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industry Research Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-primary-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white">
                Industry Research & Analysis
              </h2>
            </div>
            
            <p className="text-white/70 mb-6 leading-relaxed">
              Solusi ideal bagi bisnis skala menengah hingga besar yang ingin memahami gambaran makro industri, 
              memetakan risiko sistemik, serta merancang strategi ekspansi ke berbagai wilayah.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">In-Depth Analysis</h3>
                <p className="text-white/60">
                  Menggunakan framework <strong className="text-primary-400">PESTLE</strong> atau 
                  <strong className="text-primary-400"> PORTER Analysis</strong> secara mendalam
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Cocok Untuk</h3>
                <p className="text-white/60">
                  Usaha skala menengah-besar yang ingin mendapatkan insight makro untuk strategi 
                  pengembangan perusahaan ke berbagai wilayah
                </p>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-white/80 text-center">
                <strong className="text-primary-400">FREE CONSULTATION</strong> UNTUK MENDISKUSIKAN KEBUTUHAN ANDA
              </p>
              <p className="text-white/60 text-center text-sm mt-2">
                Kami juga menyediakan layanan bulanan berkala untuk riset industri secara periodik dan analisis berkala
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Packages Section */}
      <section ref={packagesRef} className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/market-packages-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={packagesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Paket Layanan
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Pilih paket yang sesuai dengan kebutuhan riset dan analisis pasar Anda
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                animate={packagesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`glass rounded-2xl p-6 relative ${
                  pkg.popular ? 'border-primary-500/50' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary-600 text-white text-xs font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <pkg.icon className="w-6 h-6 text-primary-400" />
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-2">{pkg.name}</h3>
                <p className="text-white/60 text-sm mb-4">{pkg.description}</p>
                
                <div className="space-y-2 mb-6">
                  {pkg.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/70 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-white/50 mb-1">Output:</p>
                  <p className="text-white/80 text-sm">{pkg.output}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Siap Memahami Pasar Anda?
            </h2>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              DILAKUKAN DENGAN SISTEM KONTRAK SECARA PERIODIK. 
              Hubungi kami untuk konsultasi gratis dan diskusikan kebutuhan riset Anda.
            </p>
            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
